import React from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography, TextField, Button } from "@mui/material";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { BonusApplicationComment } from "../../../services/bonus/types";

interface CommentComponentProps {
  comment: string; commentList: BonusApplicationComment[]; commentsAccordionExpanded: boolean;
  onCommentChange: (value: string) => void; onAddComment: (comment: string) => void;
  onAccordionToggle: () => void; showAccordion?: boolean; showTitle?: boolean;
  readOnly?: boolean; filterByHistoryId?: number | null;
}

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  CCM: { bg: "#DBEAFE", text: "#1E40AF" }, CM3: { bg: "#CCFBF1", text: "#0F766E" },
  PM:  { bg: "#CCFBF1", text: "#0F766E" }, SPM: { bg: "#CFFAFE", text: "#0E7490" },
  DCCM: { bg: "#E0E7FF", text: "#4338CA" }, DM: { bg: "#F3E8FF", text: "#7E22CE" },
  VP:  { bg: "#FEF3C7", text: "#B45309" }, Ops: { bg: "#FFEDD5", text: "#C2410C" },
  Admin: { bg: "#FEE2E2", text: "#B91C1B" },
};

const fmtDate = (date: string | Date | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const CommentComponent: React.FC<CommentComponentProps> = ({
  comment, commentList, commentsAccordionExpanded, onCommentChange, onAddComment,
  onAccordionToggle, showAccordion = true, showTitle = true, readOnly = false, filterByHistoryId = null,
}) => {
  const filtered = filterByHistoryId === null ? commentList.filter(c => !c.historyId) : commentList;

  const AddComment = () => !readOnly ? (
    <div style={{ marginBottom: 16 }}>
      <TextField fullWidth multiline rows={3} variant="outlined" placeholder="Add a comment…"
        value={comment} onChange={e => onCommentChange(e.target.value)}
        sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem", background: "var(--bg-input)", color: "var(--text-primary)" } }} />
      <Button variant="contained" onClick={() => onAddComment(comment)} disabled={!comment.trim()}
        sx={{ mt: 1, textTransform: "none", fontSize: "0.875rem", background: "var(--brand-primary)", "&:hover": { background: "var(--brand-primary-hover)" } }}>
        Add Comment
      </Button>
    </div>
  ) : null;

  const renderComments = () => {
    if (filtered.length === 0) return <p style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No comments yet</p>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(c => {
          const roleColor = ROLE_COLORS[c.roleTypeName ?? ""] ?? { bg: "var(--bg-chip)", text: "var(--text-secondary)" };
          return (
            <div key={c.commentId} style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{c.commentedBy || c.commentedByEmail || "Unknown"}</span>
                {c.roleTypeName && (
                  <span style={{ ...roleColor, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>{c.roleTypeName}</span>
                )}
                {c.statusAtTimeOfComment && (
                  <span style={{ fontSize: 11, fontWeight: 500, background: "var(--bg-chip)", color: "var(--text-secondary)", padding: "2px 8px", borderRadius: 20 }}>
                    {c.statusAtTimeOfComment.replace(/_/g, " ")}
                  </span>
                )}
                {c.commentedOn && (
                  <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>{fmtDate(c.commentedOn)}</span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>{c.commentText}</p>
              {c.isSystemGenerated && (
                <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4 }}>
                  <span style={{ fontSize: 11, color: "#B45309", fontWeight: 600 }}>AUTO</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!showAccordion) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {showTitle && <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Comments</h3>}
      {AddComment()}
      {renderComments()}
    </div>
  );

  return (
    <Accordion expanded={commentsAccordionExpanded} onChange={onAccordionToggle} sx={{ boxShadow: "none", "&:before": { display: "none" }, mb: 2 }}>
      <AccordionSummary expandIcon={<ChevronDownIcon style={{ width: 18, height: 18, color: "var(--text-muted)" }} />}
        sx={{ background: "var(--bg-surface-raised)", borderRadius: "8px", "&:hover": { background: "var(--bg-chip)" }, minHeight: 48, "& .MuiAccordionSummary-content": { margin: "12px 0" } }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--text-primary)" }}>
          Comments ({filtered.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "16px", background: "var(--bg-surface)" }}>
        {AddComment()}
        {renderComments()}
      </AccordionDetails>
    </Accordion>
  );
};

export default CommentComponent;